/**
 * useBookings Hook
 *
 * Main hook for fetching and accessing booking data.
 * Combines filter state with server data fetching.
 */

import type {
  IBooking,
  IServiceCustomer,
  IServiceEmployee,
} from "@/services/types";
import {
  bookingFiltersAtom,
  bookingsQueryAtom,
  hasActiveFiltersAtom,
  hideWeekendAtom,
} from "@/store/bookingStore";
import { calculateBookingStats, generateCardId } from "@/utils/booking";
import { formatDateKey, getVisibleDates } from "@/utils/date";
import { eachDayOfInterval, isSameDay } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";

/**
 * Booking card data - represents one employee/customer on one date
 */
export interface BookingCard {
  id: string;
  date: Date;
  employee: IServiceEmployee | null;
  customer: IServiceCustomer | null;
  bookings: IBooking[];
  excludedBookings: IBooking[];
}

// generateCardId is now imported from @/utils/booking/cardId

export function useBookings() {
  // Query state
  const [query] = useAtom(bookingsQueryAtom);
  const filters = useAtomValue(bookingFiltersAtom);
  const hasFilters = useAtomValue(hasActiveFiltersAtom);
  const hideWeekend = useAtomValue(hideWeekendAtom);

  // Raw bookings from query - stable reference to prevent unnecessary recalculations
  const bookings = useMemo(() => query.data ?? [], [query.data]);
  const isLoading = query.isLoading;
  const error = query.error;
  const refetch = query.refetch;

  // ============ DERIVED DATA ============

  /**
   * Generate date range array
   */
  const dateRange = useMemo(() => {
    return eachDayOfInterval({
      start: filters.dateRange.start,
      end: filters.dateRange.end,
    });
  }, [filters.dateRange]);

  /**
   * Filtered date range (excludes weekends if hideWeekend)
   */
  const visibleDates = useMemo(() => {
    return getVisibleDates(dateRange, hideWeekend);
  }, [dateRange, hideWeekend]);

  /**
   * Pre-index bookings by employee+date and customer+date for O(1) lookups.
   * This replaces O(n) filter operations in the nested loops below.
   */
  const bookingIndex = useMemo(() => {
    const employeeIndex = new Map<string, IBooking[]>();
    const customerIndex = new Map<string, IBooking[]>();

    for (const booking of bookings) {
      const dateKey = formatDateKey(booking.date);

      // Index by employee
      if (booking.employee?.id) {
        const key = `employee-${booking.employee.id}-${dateKey}`;
        const existing = employeeIndex.get(key);
        if (existing) {
          existing.push(booking);
        } else {
          employeeIndex.set(key, [booking]);
        }
      }

      // Index by customer
      if (booking.customer?.id) {
        const key = `customer-${booking.customer.id}-${dateKey}`;
        const existing = customerIndex.get(key);
        if (existing) {
          existing.push(booking);
        } else {
          customerIndex.set(key, [booking]);
        }
      }
    }

    return { employeeIndex, customerIndex };
  }, [bookings]);

  /**
   * Build booking cards - one card per employee/customer per date.
   * Uses pre-indexed bookings for O(1) lookups instead of O(n) filtering.
   */
  const bookingCards = useMemo((): BookingCard[] => {
    if (!hasFilters) return [];

    const cards: BookingCard[] = [];
    const projectIds = new Set(filters.projects.map((p) => p.id));

    // Employee mode
    if (filters.employees.length > 0) {
      for (const date of visibleDates) {
        const dateKey = formatDateKey(date);

        for (const employee of filters.employees) {
          // O(1) lookup instead of O(n) filter
          const key = `employee-${employee.id}-${dateKey}`;
          const dayBookings = bookingIndex.employeeIndex.get(key) ?? [];

          // Separate included (matches project filter) and excluded
          const included =
            projectIds.size > 0
              ? dayBookings.filter(
                  (b) => b.project && projectIds.has(b.project.id),
                )
              : dayBookings;

          const excluded =
            projectIds.size > 0
              ? dayBookings.filter(
                  (b) => !b.project || !projectIds.has(b.project.id),
                )
              : [];

          cards.push({
            id: generateCardId(date, employee, null),
            date,
            employee,
            customer: null,
            bookings: included,
            excludedBookings: excluded,
          });
        }
      }
    }

    // Customer mode
    if (filters.customers.length > 0 && filters.employees.length === 0) {
      for (const date of visibleDates) {
        const dateKey = formatDateKey(date);

        for (const customer of filters.customers) {
          // O(1) lookup instead of O(n) filter
          const key = `customer-${customer.id}-${dateKey}`;
          const dayBookings = bookingIndex.customerIndex.get(key) ?? [];

          // Separate included (matches project filter) and excluded
          const included =
            projectIds.size > 0
              ? dayBookings.filter(
                  (b) => b.project && projectIds.has(b.project.id),
                )
              : dayBookings;

          const excluded =
            projectIds.size > 0
              ? dayBookings.filter(
                  (b) => !b.project || !projectIds.has(b.project.id),
                )
              : [];

          cards.push({
            id: generateCardId(date, null, customer),
            date,
            employee: null,
            customer,
            bookings: included,
            excludedBookings: excluded,
          });
        }
      }
    }

    // Project-only mode: show all employees that have bookings on selected projects
    // BUG-F fix: When only projects are selected (no employees, no customers),
    // find all employees with bookings on those projects and create cards for them
    if (
      filters.projects.length > 0 &&
      filters.employees.length === 0 &&
      filters.customers.length === 0
    ) {
      // Find all unique employees from bookings that match the selected projects
      // Store the full IServiceEmployee object from the booking
      const employeesWithProjectBookings = new Map<number, IServiceEmployee>();

      for (const booking of bookings) {
        if (
          booking.project &&
          projectIds.has(booking.project.id) &&
          booking.employee
        ) {
          if (!employeesWithProjectBookings.has(booking.employee.id)) {
            employeesWithProjectBookings.set(
              booking.employee.id,
              booking.employee,
            );
          }
        }
      }

      // Create cards for each employee-date combination
      for (const date of visibleDates) {
        const dateKey = formatDateKey(date);

        for (const employee of employeesWithProjectBookings.values()) {
          // O(1) lookup
          const key = `employee-${employee.id}-${dateKey}`;
          const dayBookings = bookingIndex.employeeIndex.get(key) ?? [];

          // Only include bookings that match the project filter
          const included = dayBookings.filter(
            (b) => b.project && projectIds.has(b.project.id),
          );

          // Excluded are bookings that don't match the project filter
          const excluded = dayBookings.filter(
            (b) => !b.project || !projectIds.has(b.project.id),
          );

          cards.push({
            id: generateCardId(date, employee, null),
            date,
            employee,
            customer: null,
            bookings: included,
            excludedBookings: excluded,
          });
        }
      }
    }

    return cards;
  }, [bookingIndex, bookings, filters, visibleDates, hasFilters]);

  // ============ CARD UTILITIES ============

  /**
   * Get card by ID
   */
  const getCardById = useCallback(
    (id: string): BookingCard | undefined => {
      return bookingCards.find((c) => c.id === id);
    },
    [bookingCards],
  );

  /**
   * Get cards for a specific date
   */
  const getCardsByDate = useCallback(
    (date: Date): BookingCard[] => {
      return bookingCards.filter((c) => isSameDay(c.date, date));
    },
    [bookingCards],
  );

  /**
   * Get cards for a specific employee
   */
  const getCardsByEmployee = useCallback(
    (employeeId: number): BookingCard[] => {
      return bookingCards.filter((c) => c.employee?.id === employeeId);
    },
    [bookingCards],
  );

  /**
   * Get cards for a specific customer
   */
  const getCardsByCustomer = useCallback(
    (customerId: number): BookingCard[] => {
      return bookingCards.filter((c) => c.customer?.id === customerId);
    },
    [bookingCards],
  );

  // ============ HOUR CALCULATIONS ============

  /**
   * Calculate hours for a card
   */
  const getCardHours = useCallback((card: BookingCard) => {
    const allBookings = [...card.bookings, ...card.excludedBookings];
    const stats = calculateBookingStats(allBookings);

    return {
      projectHours: stats.project,
      totalHours: stats.total,
      absenceHours: stats.absence,
      nonProjectHours: stats.total - stats.project,
    };
  }, []);

  /**
   * Check if card is overbooked (total hours > day hours)
   */
  const isCardOverbooked = useCallback(
    (card: BookingCard, hoursInDay = 8) => {
      const { totalHours } = getCardHours(card);
      return totalHours > hoursInDay;
    },
    [getCardHours],
  );

  /**
   * Check if card is fully booked
   */
  const isCardFullyBooked = useCallback(
    (card: BookingCard, hoursInDay = 8) => {
      const { totalHours } = getCardHours(card);
      return totalHours >= hoursInDay - 0.5;
    },
    [getCardHours],
  );

  /**
   * Check if card has any bookings
   */
  const hasCardBookings = useCallback((card: BookingCard) => {
    return card.bookings.length > 0;
  }, []);

  // ============ AGGREGATIONS ============

  /**
   * Total hours across all cards
   */
  const totalBookedHours = useMemo(() => {
    return bookingCards.reduce((sum, card) => {
      const { totalHours } = getCardHours(card);
      return sum + totalHours;
    }, 0);
  }, [bookingCards, getCardHours]);

  /**
   * Total project hours across all cards
   */
  const totalProjectHours = useMemo(() => {
    return bookingCards.reduce((sum, card) => {
      const { projectHours } = getCardHours(card);
      return sum + projectHours;
    }, 0);
  }, [bookingCards, getCardHours]);

  /**
   * Total possible hours (excluding weekends if hideWeekend)
   */
  const totalPossibleHours = useCallback(
    (hoursInDay = 8) => {
      const entityCount = Math.max(
        filters.employees.length,
        filters.customers.length,
      );
      return visibleDates.length * entityCount * hoursInDay;
    },
    [filters.employees.length, filters.customers.length, visibleDates.length],
  );

  /**
   * Booking percentage
   */
  const bookingPercentage = useCallback(
    (hoursInDay = 8) => {
      const possible = totalPossibleHours(hoursInDay);
      if (possible === 0) return 0;
      return (totalBookedHours / possible) * 100;
    },
    [totalBookedHours, totalPossibleHours],
  );

  return {
    // Data
    bookings,
    bookingCards,
    dateRange,
    visibleDates,

    // Query state
    isLoading,
    error,
    refetch,
    hasFilters,

    // Card lookups
    getCardById,
    getCardsByDate,
    getCardsByEmployee,
    getCardsByCustomer,

    // Card calculations
    getCardHours,
    isCardOverbooked,
    isCardFullyBooked,
    hasCardBookings,

    // Aggregations
    totalBookedHours,
    totalProjectHours,
    totalPossibleHours,
    bookingPercentage,
  };
}
